import React, { useState, useEffect } from 'react';
import { FaTimes, FaCreditCard, FaFileInvoice, FaPause, FaPlay, FaDownload } from 'react-icons/fa';
import apiClient from '../../apiClient';
import PaymentMethodsEmbedded from './PaymentMethodsEmbedded';
import './CustomerPortalEmbedded.css';

export default function CustomerPortalEmbedded({ isOpen, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [portalReady, setPortalReady] = useState(false);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(false);
  const [diagnosticInfo, setDiagnosticInfo] = useState(null);
  
  // Tab management
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [invoices, setInvoices] = useState([]);
  const [usageSummary, setUsageSummary] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    const initializePortal = async () => {
      if (!isOpen) return;
      
      try {
        setLoading(true);
        setError('');
        
        // Load Stripe configuration (for future use)
        const configResponse = await apiClient.get('/stripe/config');
        if (!configResponse.data.publishable_key) {
          console.log('Stripe publishable key not configured');
        }
        
        // Create customer session for embedded portal
        const sessionResponse = await apiClient.post('/stripe/create-embedded-portal-session');
        
        // Handle different response types
        if (sessionResponse.data.mode === 'development') {
          // Development mode - show mock interface
          console.log('Running in development mode - Stripe not configured');
          setIsDevelopmentMode(true);
          setPortalReady(true);
          setError('');
        } else if (sessionResponse.data.use_redirect) {
          // Fallback to redirect-based portal
          console.log('Using redirect-based Customer Portal');
          setPortalReady(true);
          setError('');
        } else if (sessionResponse.data.client_secret) {
          // Full embedded mode (future implementation)
          setPortalReady(true);
        } else if (sessionResponse.data.error) {
          setError(sessionResponse.data.message || 'Failed to initialize portal');
          setPortalReady(false);
        }
        
        // Load additional data
        await loadPortalData();
        
      } catch (error) {
        console.error('Failed to initialize portal:', error);
        setError('Failed to load billing portal. Please try again.');
        
        // Run diagnostics to help debug
        try {
          const diagResponse = await apiClient.get('/stripe/diagnostic/portal-check');
          setDiagnosticInfo(diagResponse.data);
          console.log('Stripe diagnostics:', diagResponse.data);
        } catch (diagError) {
          console.error('Failed to run diagnostics:', diagError);
        }
      } finally {
        setLoading(false);
      }
    };
    
    initializePortal();
  }, [isOpen]);

  const loadPortalData = async () => {
    try {
      // Load invoices
      const invoicesResponse = await apiClient.get('/stripe/embedded-invoices');
      setInvoices(invoicesResponse.data.invoices || []);
      
      // Load usage summary
      const usageResponse = await apiClient.get('/stripe/usage-summary');
      console.log('Usage summary response:', usageResponse.data);
      setUsageSummary(usageResponse.data);
      
      // Load subscription status
      const statusResponse = await apiClient.get('/subscription/status');
      console.log('Subscription status response:', statusResponse.data);
      setSubscriptionStatus(statusResponse.data);
      
    } catch (error) {
      console.error('Failed to load portal data:', error);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setActiveTab('payment-methods');
    // Payment method update will be handled by Stripe's embedded component
  };

  const handlePauseSubscription = async () => {
    if (isDevelopmentMode) {
      alert('Subscription pause/resume is not available in development mode. Configure Stripe to enable this feature.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiClient.post('/stripe/pause-subscription', {
        action: 'pause'
      });
      
      if (response.data.success) {
        alert('Subscription paused successfully');
        await loadPortalData();
      }
    } catch (error) {
      console.error('Failed to pause subscription:', error);
      alert('Failed to pause subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (isDevelopmentMode) {
      alert('Subscription pause/resume is not available in development mode. Configure Stripe to enable this feature.');
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiClient.post('/stripe/pause-subscription', {
        action: 'resume'
      });
      
      if (response.data.success) {
        alert('Subscription resumed successfully');
        await loadPortalData();
      }
    } catch (error) {
      console.error('Failed to resume subscription:', error);
      alert('Failed to resume subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    if (isDevelopmentMode || invoiceId.startsWith('inv_mock')) {
      alert('Invoice download is not available in development mode. Configure Stripe to enable this feature.');
      return;
    }
    
    try {
      const response = await apiClient.get(`/stripe/download-invoice/${invoiceId}`);
      if (response.data.download_url && response.data.download_url !== '#') {
        window.open(response.data.download_url, '_blank');
      } else {
        alert('Invoice download URL not available.');
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
      alert('Failed to download invoice. Please try again.');
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (!isOpen) return null;

  const renderOverviewTab = () => (
    <div className="portal-tab-content">
      <h3>Subscription Overview</h3>
      
      {subscriptionStatus && subscriptionStatus.has_active_subscription && (
        <div className="subscription-overview">
          <div className="overview-card">
            <h4>Current Plan</h4>
            <p className="plan-name">{subscriptionStatus.plan_name || 'Unknown'}</p>
            <p className="plan-status">Status: {subscriptionStatus.status || 'Active'}</p>
            {subscriptionStatus.next_billing_date && (
              <p>Next billing: {formatDate(new Date(subscriptionStatus.next_billing_date).getTime() / 1000)}</p>
            )}
            {subscriptionStatus.is_free_tier && (
              <p className="free-tier-note">You're on the free plan</p>
            )}
          </div>
          
          {usageSummary && (
            <div className="overview-card">
              <h4>Usage Summary</h4>
              <div className="usage-items">
                <div className="usage-item">
                  <span>Projects:</span>
                  <span>{usageSummary.current_usage?.projects || 0} / {usageSummary.usage_limit?.projects || '∞'}</span>
                </div>
                <div className="usage-item">
                  <span>Team Members:</span>
                  <span>{usageSummary.current_usage?.employees || 0} / {usageSummary.usage_limit?.employees || '∞'}</span>
                </div>
                <div className="usage-item">
                  <span>Stakeholder Groups:</span>
                  <span>{usageSummary.current_usage?.stakeholder_groups || 0} / {usageSummary.usage_limit?.stakeholder_groups || '∞'}</span>
                </div>
              </div>
            </div>
          )}
          
          <div className="overview-actions">
            <button 
              className="portal-button primary"
              onClick={handleUpdatePaymentMethod}
            >
              <FaCreditCard /> Update Payment Method
            </button>
            
            {subscriptionStatus.status === 'active' ? (
              <button 
                className="portal-button secondary"
                onClick={handlePauseSubscription}
                disabled={loading}
              >
                <FaPause /> Pause Subscription
              </button>
            ) : subscriptionStatus.status === 'paused' ? (
              <button 
                className="portal-button primary"
                onClick={handleResumeSubscription}
                disabled={loading}
              >
                <FaPlay /> Resume Subscription
              </button>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );

  const renderInvoicesTab = () => (
    <div className="portal-tab-content">
      <h3>Invoice History</h3>
      
      {invoices.length > 0 ? (
        <div className="invoices-list">
          {invoices.map(invoice => (
            <div key={invoice.id} className="invoice-item">
              <div className="invoice-info">
                <span className="invoice-date">{formatDate(invoice.created)}</span>
                <span className="invoice-amount">{formatCurrency(invoice.amount_paid)}</span>
                <span className={`invoice-status status-${invoice.status}`}>
                  {invoice.status}
                </span>
              </div>
              <div className="invoice-actions">
                <button
                  className="download-button"
                  onClick={() => handleDownloadInvoice(invoice.id)}
                  title="Download Invoice"
                >
                  <FaDownload /> Download
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="no-invoices">No invoices found.</p>
      )}
    </div>
  );

  const handleOpenStripePortal = async () => {
    try {
      const response = await apiClient.post('/stripe/create-portal-session', {
        return_url: window.location.href
      });
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to open Stripe portal:', error);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  const renderPaymentMethodsTab = () => (
    <div className="portal-tab-content">
      <h3>Payment Methods</h3>
      
      {portalReady ? (
        <div className="stripe-portal-container">
          {isDevelopmentMode ? (
            <>
              <p className="portal-notice">
                Payment methods are not available in development mode. Configure Stripe to enable this feature.
              </p>
              <div className="portal-actions">
                <button 
                  className="portal-button secondary"
                  onClick={handleOpenStripePortal}
                >
                  <FaCreditCard /> Open Stripe Portal (External)
                </button>
              </div>
            </>
          ) : (
            <PaymentMethodsEmbedded />
          )}
        </div>
      ) : (
        <div className="loading-portal">
          <p>Loading payment methods...</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content customer-portal-embedded" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Billing & Subscription Management</h2>
          <button className="close-button" onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        {isDevelopmentMode && (
          <div className="development-notice">
            <strong>Development Mode:</strong> Stripe is not configured. Set STRIPE_SECRET_KEY and STRIPE_PUBLISHABLE_KEY to enable full functionality.
          </div>
        )}
        
        {error && (
          <div className="error-message">
            {error}
            {diagnosticInfo && diagnosticInfo.errors && diagnosticInfo.errors.length > 0 && (
              <div className="diagnostic-details">
                <strong>Diagnostic Information:</strong>
                <ul>
                  {diagnosticInfo.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
                {diagnosticInfo.recommendations && diagnosticInfo.recommendations.length > 0 && (
                  <>
                    <strong>Recommendations:</strong>
                    <ul>
                      {diagnosticInfo.recommendations.map((rec, idx) => (
                        <li key={idx}>{rec}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        
        {loading && !portalReady ? (
          <div className="modal-body loading">
            <p>Loading billing portal...</p>
          </div>
        ) : (
          <>
            <div className="portal-tabs">
              <button
                className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`tab-button ${activeTab === 'invoices' ? 'active' : ''}`}
                onClick={() => setActiveTab('invoices')}
              >
                <FaFileInvoice /> Invoices
              </button>
              <button
                className={`tab-button ${activeTab === 'payment-methods' ? 'active' : ''}`}
                onClick={() => setActiveTab('payment-methods')}
              >
                <FaCreditCard /> Payment Methods
              </button>
            </div>
            
            <div className="modal-body">
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'invoices' && renderInvoicesTab()}
              {activeTab === 'payment-methods' && renderPaymentMethodsTab()}
            </div>
          </>
        )}
      </div>
    </div>
  );
}